import { Button, Modal, Switch, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { showError } from 'components/notify';
import { showNotification } from '@mantine/notifications';
import { HiDownload } from 'react-icons/hi';
import { Publish } from 'api/publish';
import { openExport } from './export';
import { downloadBlob } from 'utils/download';
import { useState } from 'react';
type QuestionExportProps = {
  questionIds: string[];
  workspace_id: string;
  disabled?: boolean;
};
export const QuestionExport = ({
  questionIds,
  workspace_id,
  disabled
}: QuestionExportProps) => {
  const [opened, setOpened] = useState(false);
  const form = useForm({
    initialValues: {
      includeForecasts: true,
      includeComments: true,
      anonymizeUsers: false
    }
  });
  const close = () => {
    setOpened(false);
    form.reset();
    exportMutation.reset();
  };
  const exportMutation = useMutation({
    mutationFn: async (props: Publish.ExportParams) => {
      const url = Publish.exportQuestionUrl(props);
      const response = await fetch(url);
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Export failed (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error ?? errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.split('filename=')[1] ?? 'export.zip';

      // Check for errors in the zip
      const file = new File([blob], filename, {
        type: 'application/zip'
      });
      const exportData = await openExport(file);
      const exportErrors = exportData.getExportErrors();
      if (exportErrors) {
        // Parse errors and show first 3
        const errorLines = exportErrors.split('\n').filter(line => line.trim());
        const firstThreeErrors = errorLines.slice(0, 3);
        const hasMoreErrors = errorLines.length > 3;
        const errorMessage_0 = hasMoreErrors ? `${firstThreeErrors.join('\n')}\n\n... and ${errorLines.length - 3} more errors` : firstThreeErrors.join('\n');
        throw new Error(errorMessage_0);
      }
      return {
        blob,
        filename
      };
    },
    onSuccess: async ({
      blob: blob_0,
      filename: filename_0
    }) => {
      // Download the file
      downloadBlob(blob_0, filename_0);
      showNotification({
        message: 'Export completed successfully',
        color: 'green'
      });
      close();
    },
    onError: error => {
      const errorMessage_1 = error instanceof Error ? error.message : 'Unknown error occurred';
      showError(errorMessage_1, 'Export failed');
    }
  });
  return <>
      <Button variant='subtle' leftSection={<HiDownload />} disabled={disabled || questionIds.length === 0} onClick={() => {
      if (questionIds.length == 0) return;
      setOpened(true);
    }}>
        Export
      </Button>

      <Modal opened={opened} onClose={close} title={`Export Questions (${questionIds.length})`} size='lg'>
        <div className='space-y-4'>
          <div className='space-y-3'>
            <Switch label='Include forecasts' description='Include all forecasts when exporting questions' {...form.getInputProps('includeForecasts', {
            type: 'checkbox'
          })} />
            <Switch label='Include comments' description='Include all comments and discussions when exporting questions' {...form.getInputProps('includeComments', {
            type: 'checkbox'
          })} />
            <Switch label='Anonymize users' description='Replace user profiles with anonymous IDs. User names and avatars will be removed' {...form.getInputProps('anonymizeUsers', {
            type: 'checkbox'
          })} />
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='subtle' onClick={close} disabled={exportMutation.isPending}>
              Cancel
            </Button>
            <Button variant='filled' onClick={() => {
            if (questionIds.length === 0) return;
            exportMutation.mutate({
              questionIds,
              workspace_id,
              includeForecasts: form.values.includeForecasts,
              includeComments: form.values.includeComments,
              anonymizeUsers: form.values.anonymizeUsers
            });
          }} disabled={exportMutation.isPending || questionIds.length === 0} leftSection={exportMutation.isPending ? <Loader size='xs' /> : <HiDownload />}>
              {exportMutation.isPending ? 'Exporting...' : `Export ${questionIds.length} Questions`}
            </Button>
          </div>
        </div>
      </Modal>
    </>;
};